import Axios from 'axios';

import parseSurveyData from '../utils/parseSurveyData';

import {
	SURVEY_LOADING_TRUE,
	SURVEY_LOADING_FALSE,
	SURVEY_RESPONSE_GET,
	SURVEY_RESPONSE_ERROR,
	SURVEY_SCHEMAS_GET,
	SURVEY_SCHEMAS_ERROR,
	SURVEY_SCHEMA_DISPLAY_GET,
	SURVEY_SCHEMA_DISPLAY_ERROR,
	SURVEY_LIST_LOADING,
	SURVEY_LIST_GET,
	SURVEY_LIST_ERROR,
	SURVEY_DAILY_LOADING,
	SURVEY_DAILY_GET,
	SURVEY_DAILY_ERROR,
	SURVEY_LOOKUP_LOADING,
	SURVEY_LOOKUP_GET,
	SURVEY_LOOKUP_ERROR,
	SET_SURVEY_IMAGES
} from './types';

const survey_base_url = () => process.env.REACT_APP_SURVEYS_URL || process.env.REACT_APP_SERVER_URL;

export const survey_loading_true = () => dispatch => {
	dispatch({ type: SURVEY_LOADING_TRUE });
}

export const survey_loading_false = () => dispatch => {
	dispatch({ type: SURVEY_LOADING_FALSE });
}

export const get_survey_response = (response_id) => dispatch => {
	dispatch(survey_loading_true());

	const url = `${survey_base_url()}/api/surveys/responses/${response_id}/info`;

	Axios.get(url)
		.then((res) => {
			dispatch({ type: SURVEY_RESPONSE_GET, payload: res.data });
			dispatch(survey_loading_false());
		})
		.catch((err) => {
			dispatch({ type: SURVEY_RESPONSE_ERROR, payload: { type: 'survey_response', msg: err.message } });
			dispatch(survey_loading_false());
		});
}

export const clear_survey_response = () => dispatch => {
	dispatch({ type: SURVEY_RESPONSE_GET, payload: {} });
}

export const get_survey_schemas = (filters) => dispatch => {
	const params = new URLSearchParams(filters);
	const url = `${survey_base_url()}/api/surveys/schemas?${params.toString()}`;

	Axios.get(url)
		.then((res) => {
			dispatch({ type: SURVEY_SCHEMAS_GET, payload: res.data });
		})
		.catch((err) => {
			dispatch({ type: SURVEY_SCHEMAS_ERROR, payload: { type: 'survey_schemas', msg: err.message } });
		});
}

export const get_survey_schema_display = (schema_id) => dispatch => {
	const url = `${survey_base_url()}/api/surveys/schemas/${schema_id}/display`;

	Axios.get(url)
		.then((res) => {
			dispatch({ type: SURVEY_SCHEMA_DISPLAY_GET, payload: res.data });
		})
		.catch((err) => {
			dispatch({ type: SURVEY_SCHEMA_DISPLAY_ERROR, payload: { type: 'survey_schema_display', msg: err.message } });
		});
}

export const list_survey_responses = (filters = {}) => dispatch => {
	dispatch({ type: SURVEY_LIST_LOADING, payload: true });

	const url = `${survey_base_url()}/api/surveys/responses/search`;

	const body = {
		organization: filters.organization,
		survey_schema: filters.schema,
		source: filters.source,
		reference: filters.reference,
		filters: filters.fields,
		skip: filters.skip ?? 0,
		limit: filters.limit ?? 20,
		sort: filters.sort
	};

	if (filters.from !== undefined && filters.from !== null && filters.from !== '') {
		body.from = filters.from;
	}
	if (filters.to !== undefined && filters.to !== null && filters.to !== '') {
		body.to = filters.to;
	}

	// Geographic scope filters — the backend expects arrays of ObjectIds at
	// the root of the body. `codes` recorta directamente sobre `fields.code`
	// (una clave `code` dentro de `filters` es ignorada por el backend, por
	// eso la subimos aquí en vez de meterla en `filters.fields`).
	['regions', 'zones', 'depots', 'groups', 'routes', 'locations', 'codes'].forEach((key) => {
		const value = filters[key];
		if (Array.isArray(value) && value.length > 0) body[key] = value;
	});

	// `include_area` asks the backend to attach the resolved location/route/
	// depot triplet to each response. `select` is a Mongo projection that
	// trims the payload down to the fields the table actually renders —
	// inclusion-only (mixing 1 and 0 returns 400).
	if (filters.include_area) body.include_area = true;
	if (filters.select) body.select = filters.select;

	Axios.post(url, body)
		.then((res) => {
			dispatch({ type: SURVEY_LIST_GET, payload: res.data });
			dispatch({ type: SURVEY_LIST_LOADING, payload: false });
		})
		.catch((err) => {
			dispatch({ type: SURVEY_LIST_ERROR, payload: { type: 'survey_list', msg: err.message } });
			dispatch({ type: SURVEY_LIST_LOADING, payload: false });
		});
}

// Daily counts for the same scope the table renders. Accepts the same filter
// shape as `list_survey_responses`; the server ignores skip/limit/sort/select.
// When `from`/`to` are both missing the server defaults to the last 7 days in
// the configured timezone — that's why we don't set defaults on this side.
export const get_survey_daily_counts = (filters = {}) => dispatch => {
	dispatch({ type: SURVEY_DAILY_LOADING, payload: true });

	const url = `${survey_base_url()}/api/surveys/responses/counts/daily`;

	const body = {
		organization: filters.organization,
		survey_schema: filters.schema,
	};

	if (filters.from !== undefined && filters.from !== null && filters.from !== '') {
		body.from = filters.from;
	}
	if (filters.to !== undefined && filters.to !== null && filters.to !== '') {
		body.to = filters.to;
	}

	['regions', 'zones', 'depots', 'groups', 'routes', 'locations', 'codes'].forEach((key) => {
		const value = filters[key];
		if (Array.isArray(value) && value.length > 0) body[key] = value;
	});

	Axios.post(url, body)
		.then((res) => {
			dispatch({ type: SURVEY_DAILY_GET, payload: res.data });
			dispatch({ type: SURVEY_DAILY_LOADING, payload: false });
		})
		.catch((err) => {
			dispatch({ type: SURVEY_DAILY_ERROR, payload: { type: 'survey_daily', msg: err.message } });
			dispatch({ type: SURVEY_DAILY_LOADING, payload: false });
		});
}

export const lookup_survey_response = (reference) => dispatch => {
	dispatch({ type: SURVEY_LOOKUP_LOADING, payload: true });

	const url = `${survey_base_url()}/api/surveys/responses/lookup`;

	return Axios.get(url, { params: { reference } })
		.then((res) => {
			dispatch({ type: SURVEY_LOOKUP_GET, payload: res.data });
			dispatch({ type: SURVEY_LOOKUP_LOADING, payload: false });
			return res.data;
		})
		.catch((err) => {
			dispatch({ type: SURVEY_LOOKUP_ERROR, payload: { type: 'survey_lookup', msg: err.message } });
			dispatch({ type: SURVEY_LOOKUP_LOADING, payload: false });
			throw err;
		});
}

export const set_survey_images = (surveyResponse, surveySchemaDisplay) => dispatch => {
	const images = [];

	const parsed = parseSurveyData(
		surveySchemaDisplay ?? [],
		surveyResponse?.fields ?? {},
		surveyResponse?.photos ?? []
	);

	for (const field of parsed) {
		if (field.type === 'image') {
			const ids = Array.isArray(field.value) ? field.value : [];
			for (const imageId of ids) {
				if (imageId) {
					images.push({ type: field.label ?? field.field_name ?? 'photo', imageId });
				}
			}
		}
		else if (field.type === 'submodule') {
			const items = Array.isArray(field.value) ? field.value : [];
			items.forEach((item, itemIdx) => {
				const itemFields = Array.isArray(item?.fields) ? item.fields : [];
				const itemPhotos = Array.isArray(item?.photos) ? item.photos : [];

				itemFields.filter(f => f.type === 'image').forEach((f) => {
					if (f.value) {
						images.push({ type: f.label ?? f.field_name, imageId: f.value });
					}
				});

				itemPhotos.forEach((photo) => {
					if (photo?.imageId) {
						const parentLabel = field.label ?? field.field_name;
						const photoLabel = photo.field ? ` - ${photo.field}` : '';
						images.push({ type: `${parentLabel} ${itemIdx + 1}${photoLabel}`, imageId: photo.imageId });
					}
				});
			});
		}
	}

	dispatch({ type: SET_SURVEY_IMAGES, payload: images });
}
